import { useMemo, useState } from 'react';
import { MenuItem } from '@carbon/react';
import {
  ExportMenuButton,
  SingleLayerSpatialExportDialog,
  type SpatialLayerOption,
} from 'components/export-spatial-data';
import { useAreasSpatialExport } from './hooks';
import { AREA_EXPORT_ATTRIBUTES } from './catalog-utils';
import type { AreaGeometriesQueryParams } from './atoms';

// The single exportable layer id the backend keys this export on
// (GisExport::Sources::AreaGeometry). The Areas page has one spatial dataset — its area polygons.
const AREAS_LAYER_ID = 'areas';

interface AreasSpatialExportProps {
  /** The current Areas catalog filter params; the export covers exactly this filtered set. */
  params: AreaGeometriesQueryParams;
  /** The filtered record count shown beside the layer. */
  totalCount?: number;
}

/**
 * The Areas page "Export" dropdown (a page-header action beside Create New Area). A shared
 * {@link ExportMenuButton} with a single "Spatial Data" item — the GIS export of the filtered area
 * polygons via the shared {@link SingleLayerSpatialExportDialog}. (Areas' tabular/Excel export already
 * lives on the catalog toolbar's own ExportButton, not here; this page-header dropdown stays a dropdown to
 * mirror the Type Curves "Export" action. Whether Areas should instead keep a bare button is an open
 * product question raised in review, not yet tracked on a ticket.) Scoped to the current catalog filters;
 * disabled with a reason when no area matches.
 *
 * @param {AreasSpatialExportProps} props - The current filter params + filtered record count.
 * @return {JSX.Element} The "Export" menu button and the spatial-export dialog.
 */
export function AreasSpatialExport({
  params,
  totalCount,
}: AreasSpatialExportProps) {
  const controller = useAreasSpatialExport();
  const [open, setOpen] = useState(false);

  const layer = useMemo<SpatialLayerOption>(
    () => ({
      id: AREAS_LAYER_ID,
      label: 'Areas',
      count: totalCount,
      attributes: AREA_EXPORT_ATTRIBUTES.map((attribute) => ({ ...attribute })),
    }),
    [totalCount]
  );

  const disabled = !controller.userId || totalCount === 0;
  const disabledReason =
    totalCount === 0 ? 'No areas match the current filters' : undefined;

  return (
    <>
      <ExportMenuButton disabled={disabled} disabledReason={disabledReason}>
        {/* No item-level `disabled`: an item inside a disabled trigger is
            unreachable, so the trigger's disabled state is the only gate. */}
        <MenuItem label="Spatial Data" onClick={() => setOpen(true)} />
      </ExportMenuButton>
      <SingleLayerSpatialExportDialog
        open={open}
        onClose={() => setOpen(false)}
        layer={layer}
        filters={params as Record<string, unknown>}
        controller={controller}
      />
    </>
  );
}
